import { Injectable } from '@angular/core';
import { ToastController, AlertController, LoadingController } from '@ionic/angular/standalone';
import { createServiceLogger } from '../utils/logger.util';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { LazyGlitchTipService } from './lazy-glitchtip.service';

export interface ErrorContext {
  operation: string;
  component?: string;
  userId?: string;
  additionalData?: Record<string, any>;
}

export interface LoadingState {
  isLoading: boolean;
  operation: string;
  startTime: number;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  private logger = createServiceLogger('ErrorHandlerService');
  private loadingStates = new Map<string, LoadingState>();
  private loadingSubject = new BehaviorSubject<Map<string, LoadingState>>(new Map());
  private readonly DEFAULT_TIMEOUT = 30000; // 30 seconds
  private readonly RETRY_ATTEMPTS = 3;
  private readonly RETRY_DELAY = 1000; // 1 second

  readonly loadingStates$ = this.loadingSubject.asObservable();

  constructor(
    private toastController: ToastController,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private glitchTipService: LazyGlitchTipService
  ) {}

  /**
   * Handle API failures gracefully with retry logic
   */
  async handleApiError(error: any, context: ErrorContext, showUserMessage = true): Promise<void> {
    this.logger.error('API error occurred', {
      operation: context.operation,
      component: context.component,
      userId: context.userId
    }, error);

    // Enhanced GlitchTip logging with proper categorization
    if (error?.status !== undefined || error?.code?.includes('firestore/')) {
      // This is likely a Firestore or HTTP error
      if (error?.code?.includes('firestore/')) {
        this.glitchTipService.captureFirestoreError(error, {
          operation: this.getFirestoreOperation(context.operation),
          collection: this.extractCollectionFromContext(context),
          userId: context.userId,
          additionalData: context.additionalData,
        });
      } else {
        this.glitchTipService.captureApiError(error, {
          operation: context.operation,
          endpoint: this.extractEndpointFromError(error),
          method: 'GET', // Default, could be enhanced
          statusCode: error?.status,
          additionalData: context.additionalData,
        });
      }
    } else if (!this.isRoutineError(error)) {
      // Only capture non-routine errors to reduce noise
      this.glitchTipService.captureException(error, {
        tags: {
          operation: context.operation,
          component: context.component,
        },
        extra: {
          ...context.additionalData,
          errorMessage: error?.message,
          errorCode: error?.code,
        },
        user: context.userId ? { id: context.userId } : undefined,
      });
    }

    if (showUserMessage) {
      await this.showErrorToast(this.getErrorMessage(error, context.operation));
    }
  }

  /**
   * Retry operation with exponential backoff
   */
  async retryOperation<T>(
    operation: () => Promise<T>,
    context: ErrorContext,
    maxRetries = this.RETRY_ATTEMPTS
  ): Promise<T> {
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        this.logger.warn('Retry attempt failed', {
          operation: 'retry_attempt_failed',
          metadata: { attempt, targetOperation: context.operation }
        });

        if (attempt === maxRetries) {
          await this.handleApiError(error, context);
          throw error;
        }

        // Exponential backoff: 1s, 2s, 4s, etc.
        const delay = this.RETRY_DELAY * Math.pow(2, attempt - 1);
        await this.delay(delay);
      }
    }

    throw lastError;
  }

  /**
   * Execute operation with loading state and timeout
   */
  async executeWithLoading<T>(
    operation: () => Promise<T>,
    context: ErrorContext,
    timeout = this.DEFAULT_TIMEOUT
  ): Promise<T> {
    const operationId = `${context.operation}-${Date.now()}`;

    try {
      this.setLoadingState(operationId, context.operation, true);

      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          const timeoutError = new Error(`Operation ${context.operation} timed out after ${timeout}ms`);
          reject(timeoutError);
        }, timeout);
      });

      // Race between operation and timeout
      const result = await Promise.race([operation(), timeoutPromise]);

      return result;
    } catch (error) {
      await this.handleApiError(error, context);
      throw error;
    } finally {
      this.setLoadingState(operationId, context.operation, false);
    }
  }

  /**
   * Show confirmation dialog for destructive actions
   */
  async confirmDestructiveAction(
    title: string,
    message: string,
    confirmText = 'Delete',
    cancelText = 'Cancel'
  ): Promise<boolean> {
    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header: title,
        message: message,
        buttons: [
          {
            text: cancelText,
            role: 'cancel',
            handler: () => resolve(false),
          },
          {
            text: confirmText,
            role: 'destructive',
            handler: () => resolve(true),
          },
        ],
      });

      await alert.present();
    });
  }

  /**
   * Show error toast with consistent styling
   */
  async showErrorToast(message: string, duration = 5000): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration,
      color: 'danger',
      position: 'bottom',
      buttons: [
        {
          text: 'Close',
          role: 'cancel',
        },
      ],
    });

    await toast.present();
  }

  /**
   * Show success toast
   */
  async showSuccessToast(message: string, duration = 3000): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration,
      color: 'success',
      position: 'bottom',
    });

    await toast.present();
  }

  /**
   * Show IndexedDB connection error alert with device restart guidance
   */
  async showIndexedDBConnectionError(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Storage Connection Error',
      message: 'There was a problem connecting to the storage system. Please try restarting the app or your device.',
      buttons: [
        {
          text: 'Understood',
          role: 'cancel',
        },
      ],
    });

    await alert.present();
  }

  /**
   * Validate object before operations
   */
  validateObject(obj: any, requiredFields: string[], context: string): boolean {
    if (!obj || typeof obj !== 'object') {
      this.logger.error('Object validation failed - invalid object', {
        operation: 'validate_object',
        metadata: { context, objectType: typeof obj }
      });
      return false;
    }

    const missingFields = requiredFields.filter((field) => {
      const value = this.getNestedProperty(obj, field);
      return value === undefined || value === null || value === '';
    });

    if (missingFields.length > 0) {
      this.logger.error('Object validation failed - missing fields', {
        operation: 'validate_object',
        metadata: { context, missingFields }
      });
      return false;
    }

    return true;
  }

  /**
   * Sanitize input to prevent XSS
   */
  sanitizeInput(input: string): string {
    if (typeof input !== 'string') {
      return '';
    }

    return input
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;')
      .trim();
  }

  /**
   * Sanitize object recursively
   */
  sanitizeObject(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (typeof obj === 'string') {
      return this.sanitizeInput(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.sanitizeObject(item));
    }

    if (typeof obj === 'object') {
      const sanitized: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          sanitized[key] = this.sanitizeObject(obj[key]);
        }
      }
      return sanitized;
    }

    return obj;
  }

  /**
   * Check if operation is currently loading
   */
  isLoading(operation?: string): boolean {
    if (operation) {
      return Array.from(this.loadingStates.values()).some((state) => state.operation === operation && state.isLoading);
    }
    return this.loadingStates.size > 0;
  }

  /**
   * Get all current loading operations
   */
  getLoadingOperations(): string[] {
    return Array.from(this.loadingStates.values())
      .filter((state) => state.isLoading)
      .map((state) => state.operation);
  }

  private setLoadingState(id: string, operation: string, isLoading: boolean): void {
    if (isLoading) {
      this.loadingStates.set(id, {
        isLoading: true,
        operation,
        startTime: Date.now(),
      });
    } else {
      this.loadingStates.delete(id);
    }

    this.loadingSubject.next(new Map(this.loadingStates));
  }

  private getErrorMessage(error: any, operation: string): string {
    // IndexedDB connection errors
    if (this.isIndexedDBConnectionError(error)) {
      return 'Storage connection error. Please try again or restart the app.';
    }

    // Network errors
    if (error?.status === 0 || error?.message?.includes('offline')) {
      return 'Network error. Please check your internet connection.';
    }

    // Authentication errors
    if (error?.status === 401 || error?.code === 'unauthenticated') {
      return 'Authentication error. Please log in again.';
    }

    // Permission errors
    if (error?.status === 403 || error?.code === 'permission-denied') {
      return 'Permission denied. You don\'t have access to this resource.';
    }

    // Timeout errors
    if (error?.message?.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }

    // Firebase errors
    if (error?.code?.startsWith('firestore/')) {
      return 'Database error. Please try again later.';
    }

    // Generic error with operation context
    return `Operation failed: ${operation}. Please try again.`;
  }

  private getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private getFirestoreOperation(operation: string): 'read' | 'write' | 'delete' | 'query' | 'transaction' | 'batch' {
    const operationLower = operation.toLowerCase();
    if (operationLower.includes('delete') || operationLower.includes('remove')) return 'delete';
    if (operationLower.includes('update') || operationLower.includes('create') || operationLower.includes('save')) return 'write';
    if (operationLower.includes('query') || operationLower.includes('search')) return 'query';
    if (operationLower.includes('transaction')) return 'transaction';
    if (operationLower.includes('batch')) return 'batch';
    return 'read'; // Default fallback
  }

  private extractCollectionFromContext(context: ErrorContext): string {
    // Try to extract collection name from operation or additional data
    const operation = context.operation.toLowerCase();
    if (operation.includes('bedek')) return 'bedek_reports';
    if (operation.includes('retivut')) return 'retivut_reports';
    if (operation.includes('deal') || operation.includes('crm')) return 'deals';
    if (context.additionalData?.collection) return context.additionalData.collection;
    return 'unknown';
  }

  private extractEndpointFromError(error: any): string {
    // Try to extract endpoint from error object
    if (error?.url) return error.url;
    if (error?.config?.url) return error.config.url;
    if (error?.request?.responseURL) return error.request.responseURL;
    return 'unknown';
  }

  /**
   * Check if error is an IndexedDB connection error
   */
  private isIndexedDBConnectionError(error: any): boolean {
    if (!error) return false;

    const errorMessage = error?.message?.toLowerCase() || error?.toString?.()?.toLowerCase() || '';
    const connectionErrors = [
      'connection to indexed database server lost',
      'a mutation operation was attempted on a database that did not allow mutations',
      'the database connection is closing',
      'database has been closed',
      'invalidstateerror',
      'unknownerror'
    ];

    return connectionErrors.some(pattern => errorMessage.includes(pattern));
  }

  /**
   * Check if error is routine and shouldn't be reported to GlitchTip
   */
  private isRoutineError(error: any): boolean {
    if (!error) return true;

    const errorMessage = error?.message?.toLowerCase() || '';
    const errorName = error?.name?.toLowerCase() || '';

    // Timeout errors are routine during poor connectivity
    if (errorName === 'timeouterror' ||
        errorMessage.includes('timeout') ||
        errorMessage.includes('timed out')) {
      return true;
    }

    // Network connectivity errors are routine
    if (errorMessage.includes('failed to fetch') ||
        errorMessage.includes('network request failed') ||
        errorMessage.includes('network error') ||
        errorMessage.includes('connection refused') ||
        errorMessage.includes('no internet')) {
      return true;
    }

    // Abort errors from cancelled requests
    if (errorName === 'aborterror' ||
        errorMessage.includes('aborted') ||
        errorMessage.includes('cancelled')) {
      return true;
    }

    // CORS errors in development
    if (errorMessage.includes('cors') ||
        errorMessage.includes('cross-origin')) {
      return true;
    }

    // IndexedDB connection errors are routine on iOS
    if (this.isIndexedDBConnectionError(error)) {
      return true;
    }

    return false;
  }
}
